import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { Box, Grid, Typography } from "@material-ui/core";

import {
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  LabelList,
} from "recharts";

import {
  unrealizedTradingRevenuesBars,
  unrealizedTradingRevenuesGraphItems,
} from "../../../constants";
import { COLORS } from "../../../../../theme/colors";
import { Button } from "../../../../../components/base";
import { ShaDowBox } from "../../../../../components/boxes";
import { GraphFooter, NoDataCard } from "../../../../../components/parts";
import { currencyFormatter, getData, onlyZeroValuesInArray } from "../../../../../services";

const topLabel = (props) => {
  const { x, y, value, name, height, width } = props;

  if (!value) return null;

  return (
    <g>
      <text
        x={x}
        y={y}
        dy={height - 20}
        dx={width / 2}
        fill={COLORS.darks[0][100]}
        fontSize={18}
        textAnchor="middle"
      >
        {value ? name : ""}
      </text>
    </g>
  );
};

const rightLabel = (props) => {
  const { x, y, height, value, name } = props;
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);

  if (!value) return null;

  return (
    <g>
      <text
        x={x}
        y={y}
        dy={height / 2 + 25}
        dx={45}
        fill={COLORS.red}
        style={{ fontSize: 24, fontWeight: 700 }}
      >
        {currencyFormatter(value, currencySymbol)}
        <tspan style={{ fontSize: 14, fontWeight: 400 }}>/{name}</tspan>
      </text>
    </g>
  );
};

export const AvgUnrealizedPraasTradingRevenues = ({ buildingId, complexId }) => {
  const [chartData, setChartData] = useState([]);
  const title = "Avg. Unrealized PRaaS Trading Revenues";

  let url = `/praas/v2/buildings/properties/unrealized-trading/`;

  if (buildingId) {
    url = `/praas/v2/buildings/${buildingId}/properties/unrealized-trading/`;
  } else if (complexId) {
    url = `/praas/v2/complexes/${complexId}/properties/unrealized-trading/`;
  }

  useEffect(() => {
    getData(url, (data) => {
      if (!onlyZeroValuesInArray(Object.values(data.monthly).concat(Object.values(data.yearly)))) {
        const monthly =
          data.monthly.unconnected_tenants +
          data.monthly.economic_vacancy +
          data.monthly.physical_vacancy;
        const yearly =
          data.yearly.unconnected_tenants +
          data.yearly.economic_vacancy +
          data.yearly.physical_vacancy;

        const highest = monthly > yearly ? monthly : yearly;
        const negative = highest + (highest * 10) / 100;

        setChartData([
          {
            name: "A",
            unconnected_tenants: 0,
            economic_vacancy: 0,
            physical_vacancy: 0,
            positive: 0,
            negative: -negative,
            total: 0,
          },
          {
            name: "Monthly",
            unconnected_tenants: -data.monthly.unconnected_tenants,
            economic_vacancy: -data.monthly.economic_vacancy,
            physical_vacancy: -data.monthly.physical_vacancy,
            positive: 0,
            negative: -negative,
            total: -monthly,
          },
          {
            name: "Yearly",
            unconnected_tenants: -data.yearly.unconnected_tenants,
            economic_vacancy: -data.yearly.economic_vacancy,
            physical_vacancy: -data.yearly.physical_vacancy,
            positive: 0,
            negative: -negative,
            total: -yearly,
          },
          {
            name: "B",
            unconnected_tenants: 0,
            economic_vacancy: 0,
            physical_vacancy: 0,
            positive: 0,
            negative: -negative,
            total: 0,
          },
        ]);
      }
    });

    return () => setChartData([]);
  }, [complexId, buildingId]);

  if (!chartData.length) return <NoDataCard title={title} />;

  return (
    <ShaDowBox fullHeight>
      <Box style={{ padding: 24 }}>
        <Grid container justifyContent="space-between" style={{ marginBottom: 40 }}>
          <Typography variant="h4">{title}</Typography>
          <Button text="Optimize Tenants Profitability" />
        </Grid>
        <Box style={{ marginTop: 24, height: 250, position: "relative" }}>
          <ResponsiveContainer>
            <ComposedChart
              width="100%"
              height="100%"
              data={chartData}
              stackOffset="sign"
              margin={{
                top: 40,
              }}
            >
              <XAxis dataKey="name" scale="point" hide={true} />
              <YAxis hide={true} />
              <ReferenceLine y={0} stroke="#000" />
              <Area
                type="monotone"
                dataKey="positive"
                fill={COLORS.purples[10]}
                stroke={COLORS.purples[10]}
              />
              <Area
                type="monotone"
                dataKey="negative"
                fill="rgba(225, 201, 167, 0.2)"
                stroke="rgba(225, 201, 167, .2)"
              />
              {unrealizedTradingRevenuesBars.map((bar, i) => (
                <Bar
                  key={i}
                  dataKey={bar.dataKey}
                  barSize={30}
                  fill={bar.fill}
                  stackId="stack"
                  stroke="#000"
                  strokeDasharray="3 3"
                >
                  {bar.dataKey === "unconnected_tenants" && (
                    <LabelList dataKey="total" content={(props) => topLabel(props)} />
                  )}
                  {bar.dataKey === "economic_vacancy" && (
                    <LabelList dataKey="total" content={(props) => rightLabel(props)} />
                  )}
                </Bar>
              ))}
            </ComposedChart>
          </ResponsiveContainer>
          <GraphFooter
            items={unrealizedTradingRevenuesGraphItems}
            style={{ position: "absolute", bottom: 10 }}
            typographyStyle={{ fontSize: 12, fontWeight: 700 }}
          />
        </Box>
      </Box>
    </ShaDowBox>
  );
};
